<template>
  <div v-if="booking">
    <top-navigation
      class="hide-on-print"
      :route-object="previousRoute"
      :title="$t('printTickets.printTickets')"
      iconTitle="fa-print"
    />

    <v-row class="hide-on-print">
      <!-- print button -->
      <v-col class="col-6">
        <v-btn color="green" class="white--text" large @click="print()">
          <span class="fal fa-print fa-2x mr-2" />
          {{ $t('general.print') }}
        </v-btn>
      </v-col>

      <!-- refresh button -->
      <v-col class="col-6 text-right">
        <v-btn
          color="magenta"
          class="white--text"
          large
          @click="refreshBooking()"
        >
          <span class="fal fa-sync fa-2x" />
        </v-btn>
      </v-col>
    </v-row>

    <!-- tickets -->
    <v-row class="justify-center font-weight-thin display-block">
      <v-col class="A4 py-0 justify-center-a4">
        <!-- iterate user -->
        <v-row
          v-for="(bookingEntryGroup, uid) in bookingEntryGroups"
          :key="uid"
          class="page-break-after display-block"
        >
          <v-col class="col-12 pa-0">
            <!-- iterate booking entries of user -->
            <v-row
              v-for="(bookingEntry, bookingEntryIndex) in bookingEntryGroup"
              :key="bookingEntryIndex"
              class="pt-4 pb-4 px-8 no-gutters A4-height-one-third"
              :class="{
                'page-break-after': (bookingEntryIndex + 1) % 3 === 0,
                'border-bottom border-width-2':
                  (bookingEntryIndex + 1) % 3 !== 0,
              }"
            >
              <!-- title -->
              <v-col class="col-8 headline-large align-self-end pb-2">
                <!-- cwc -->
                <span v-if="bookingEntry instanceof CwcBookingEntry">
                  {{ $t('printTickets.co2Compensation') }}
                </span>
                <span v-else>
                  <span v-if="bookingEntry.printCount">
                    {{ bookingEntry.printCount }}x
                  </span>
                  {{ bookingEntry.getProductTranslation() }}
                </span>
              </v-col>

              <!-- destination logo -->
              <v-col class="col-4 align-self-end pb-2">
                <img
                  v-if="
                    booking.getDestination() &&
                    booking.getDestination().getLogo()
                  "
                  class="destination-logo"
                  :src="booking.getDestination().getLogo()"
                />
              </v-col>

              <!-- product definition --->
              <v-col class="col-8 pr-8">
                <p v-if="bookingEntry instanceof CwcBookingEntry">
                  {{ $t('printTickets.cwcExplanation') }}
                </p>
                <p
                  v-else
                  :class="{
                    'headline-standard': !bookingEntry.hasAnyBarcode(),
                  }"
                >
                  {{ bookingEntry.getProductDefinitionTranslation() }}
                </p>
              </v-col>

              <!-- user -->
              <v-col class="col-4">
                <!-- do not print name, if it was booked by cash desk -->
                <div
                  v-if="
                    bookingEntry.getUser().getId() !==
                    $store.getters.getAppUserInstance().getId()
                  "
                >
                  <p class="mb-0">{{ bookingEntry.getUser().getCompany() }}</p>
                  <p class="mb-0">{{ bookingEntry.getUser().getFullName() }}</p>
                  <p>
                    {{ bookingEntry.getUser().getBirthdate() | formatDate }}
                  </p>
                </div>
              </v-col>

              <!-- barcodes / cwc logo -->
              <v-col
                v-if="bookingEntry instanceof CwcBookingEntry"
                class="col-8 align-self-center"
              >
                <v-row>
                  <v-col class="col-6">
                    <v-img :src="cwcLogo" :aspect-ratio="undefined" eager />
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                v-else-if="
                  bookingEntry.isReservation() && !bookingEntry.hasAnyBarcode()
                "
                class="col-8"
              >
                <!-- niesen specific hardcode -->
                <v-row
                  v-if="
                    $store.getters.getAppDestinationInstance().getSlug() ===
                    'niesen'
                  "
                  class="no-gutters"
                >
                  <v-col class="mt-4">
                    <p>
                      {{ $t('printTickets.niesenReservationHint') }}
                    </p>
                  </v-col>
                </v-row>
              </v-col>
              <v-col v-else class="col-8 align-self-center">
                <!-- skidata -->
                <v-row
                  v-if="bookingEntry.getSkidata()"
                  :set="(skidata = bookingEntry.getSkidata())"
                >
                  <!-- state is ok -->
                  <v-col v-if="skidata.getState() === 0" class="col-12">
                    <qrcode-vue
                      :value="skidata.getBarcode()"
                      level="H"
                      :size="140"
                    />
                  </v-col>
                  <!-- state not ok -->
                  <v-col v-else class="col-12 pa-0">
                    <p>
                      <span
                        class="fal fa-exclamation-triangle mr-2 red--text"
                      />{{ $t('printTickets.barcodeError') }}
                    </p>
                    <p>
                      <span class="fal fa-info-circle mr-2 primary--text" />{{
                        $t('printTickets.reason')
                      }}: {{ skidata.getStateDescription() }}
                    </p>
                  </v-col>
                </v-row>

                <!-- novatouch -->
                <v-row v-if="bookingEntry.getNovatouch()">
                  <!-- check state -->
                  <v-col
                    v-if="bookingEntry.getNovatouch().getState() === 0"
                    class="col-12"
                  >
                    <qrcode-vue
                      :value="bookingEntry.getNovatouch().getBarcode()"
                      level="H"
                      :size="140"
                    />
                  </v-col>

                  <!-- state is not ok -->
                  <v-col v-else class="col-12 pa-0">
                    <p>
                      <span
                        class="fal fa-exclamation-triangle mr-2 red--text"
                      />{{ $t('printTickets.barcodeError') }}
                    </p>
                    <p>
                      <span class="fal fa-info-circle mr-2 primary--text" />{{
                        $t('printTickets.reason')
                      }}: {{ $t('printTickets.novatouchError') }}
                    </p>
                  </v-col>
                </v-row>
              </v-col>

              <v-col class="col-4 align-self-center">
                <p class="mb-1">
                  <!-- required basket entry => inclusive -->
                  <span
                    v-if="
                      bookingEntry.getRequiredEntry() &&
                      !bookingEntry.getPrice()
                    "
                  >
                    {{ $t('createBooking.incl') }}
                  </span>
                  <!-- price is 0 => offered -->
                  <span v-else-if="!bookingEntry.getPrice() && bookingEntry.getPrice() !== 0">
                    {{ $t('basket.offered') }}
                  </span>
                  <!-- normal price -->
                  <span v-else>
                    {{ $t('general.price') }}:
                    <span v-if="bookingEntry.printCount">
                      {{ bookingEntry.printCount }}x
                    </span>
                    {{ bookingEntry.getPrice() | currency }}
                  </span>
                </p>

                <!-- iterate vats -->
                <p
                  v-for="(vat, vatIndex) in bookingEntry.getVatsArray()"
                  :key="vatIndex"
                  class="mb-0 content-5 font-weight-thin"
                >
                  <span v-if="vat.getPrice()">
                    {{ $t('adminBasket.vat') }} {{ vat.getRate() }} %:
                    <span v-if="bookingEntry.printCount">
                      {{ bookingEntry.printCount }}x
                    </span>
                    {{ vat.getPrice() | currency }}
                  </span>
                </p>
                <!-- vat number -->
                <p>
                  {{ booking.getDestination().getTaxNr() }}
                </p>
                <!-- validity -->
                <p
                  v-if="bookingEntry instanceof BookingEntry"
                  class="mb-0 mt-2"
                >
                  <!-- same start and end date -->
                  {{ $t('printTickets.valid') }}:
                  {{ bookingEntry.getStartDate() | formatDate }}
                  <span
                    v-if="
                      !moment(bookingEntry.getStartDate()).isSame(
                        bookingEntry.getEndDate(),
                        'day'
                      )
                    "
                  >
                    - {{ bookingEntry.getEndDate() | formatDate }}
                  </span>
                </p>
                <!-- booking number -->
                <p class="mb-0">
                  {{
                    $t('printTickets.bookingNumber', {
                      bookingNumber: booking.getId(),
                    })
                  }}
                </p>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TopNavigation from '../../../components-shared/menus/TopNavigation'
import _ from 'lodash'
import PickupBarcode from '../PickupBarcode'
import Booking from '../../../classes-shared/bookings/Booking'
import QrcodeVue from 'qrcode.vue'
import CwcBookingEntry from '../../../classes-shared/bookings/CwcBookingEntry'
import BookingEntry from '../../../classes-shared/bookings/BookingEntry'
import cwcLogo from '../../../assets/icons/cwc.svg'
import moment from 'moment'

export default {
  name: 'PrintTickets',
  components: { TopNavigation, QrcodeVue },
  data() {
    return {
      booking: null,
      previousRoute: null,
      CwcBookingEntry,
      BookingEntry,
      cwcLogo,
      moment,
    }
  },

  computed: {
    // group BookingEntries by users
    bookingEntryGroups() {
      if (this.booking) {
        let bookingEntries = this.booking
          .getBookingEntries()
          .filter((tmpBookingentry) => !tmpBookingentry.hasBeenCancelled())
        let groups = _.groupBy(bookingEntries, (bookingEntry) => {
          if (bookingEntry.getRequiredEntry())
            return bookingEntry.getRequiredEntry()
          // has any booking entry me as a required entry? => even if it's transferable put them together
          if (
            bookingEntries.find(
              (findEntry) =>
                findEntry.getRequiredEntry() === bookingEntry.getId()
            )
          )
            return bookingEntry.getId()
          if (bookingEntry.isTransferableTicket()) return 'transferable'
          return bookingEntry.getId()
        })
        // the following uncommented code is DEPRECATED since 26.06.2020
        // let groups = _.groupBy(this.booking.getBookingEntries(), bookingEntry => bookingEntry.getUser().getId())
        // group booking entries (eg. reservations with same product definition and user)
        for (let groupId in groups) {
          let bookingEntries = groups[groupId]
          for (let a = 0; a < bookingEntries.length; a++) {
            let bookingEntry = bookingEntries[a]
            // reset print count
            // fixes https://pricenow.atlassian.net/browse/T1-923
            if (bookingEntry.printCount) delete bookingEntry.printCount
            for (let b = a + 1; b < bookingEntries.length; b++) {
              let bookingEntryB = bookingEntries[b]
              if (
                !bookingEntry.hasAnyBarcode() &&
                bookingEntry.isTransferableTicket() &&
                bookingEntry.getProductDefinitionId() ===
                  bookingEntryB.getProductDefinitionId()
              ) {
                bookingEntryB['deleteForPrint'] = true
                if (bookingEntry.printCount) bookingEntry.printCount += 1
                else bookingEntry['printCount'] = 2
              }
            }
          }
          bookingEntries.sort((a, b) => a.getSortOrder() - b.getSortOrder())
          bookingEntries = _.remove(
            bookingEntries,
            (bookingEntry) => bookingEntry.deleteForPrint
          )
        }
        // add cwc
        if (this.booking.getCwc()) {
          let cwcInstance = new CwcBookingEntry(
            this.booking.getCwc(),
            this.booking.getBuyer()
          )
          let groupKeys = Object.keys(groups)
          // add cwc to the last group
          groupKeys.length
            ? groups[groupKeys[groupKeys.length - 1]].push(cwcInstance)
            : (groups['cwc'] = [cwcInstance])
        }
        return groups
      }
      return []
    },
  },

  async created() {
    // catch booking instance (Type: Booking.js)
    if (this.$route.params.booking) this.booking = this.$route.params.booking
    // if only booking id is provided
    else if (this.$route.params.id) {
      this.booking = new Booking({ id: this.$route.params.id })
      await this.booking.fetchBooking()
    }

    // check if we have any booking now
    if (!this.booking) throw new Error('No Booking.js instance passed!')

    // catch route to go back
    this.previousRoute = this.$route.params.previousRoute
    if (!this.previousRoute) throw new Error('No route object passed!')
  },

  methods: {
    print() {
      window.print()
    },

    async refreshBooking() {
      await this.booking.fetchBooking()
    },
  },
}
</script>
